/**
 * TradeList.tsx
 *
 * Component for displaying individual trades from a backtest strategy.
 */

import React, { useState, useMemo } from 'react';
import { TradeResult, formatExitReason } from '../../types/backtest';

interface TradeListProps {
  trades: TradeResult[];
  strategyName: string;
}

const TradeList: React.FC<TradeListProps> = ({ trades, strategyName }) => {
  const [sortField, setSortField] = useState<keyof TradeResult>('entry_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [filterReason, setFilterReason] = useState<string>('all');
  const [showAll, setShowAll] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);

  const formatCurrency = (value: number): string => {
    const sign = value >= 0 ? '+' : '';
    if (Math.abs(value) >= 1000) {
      return `${sign}$${(value / 1000).toFixed(1)}k`;
    }
    return `${sign}$${value.toFixed(0)}`;
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: '2-digit'
    });
  };

  // Get unique exit reasons for filter
  const exitReasons = useMemo(() => {
    const reasons = new Set(trades.map(t => t.exit_reason));
    return Array.from(reasons).sort();
  }, [trades]);

  // Filter and sort trades
  const processedTrades = useMemo(() => {
    let result = [...trades];

    // Filter
    if (filterReason !== 'all') {
      result = result.filter(t => t.exit_reason === filterReason);
    }

    // Sort
    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      return 0;
    });

    return result;
  }, [trades, sortField, sortDirection, filterReason]);

  const displayedTrades = showAll ? processedTrades : processedTrades.slice(0, 10);

  const handleSort = (field: keyof TradeResult) => {
    if (field === sortField) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleCopyTable = async () => {
    const headers = ['Entry Date', 'Exit Date', 'Strike', 'Delta', 'DTE', 'Entry', 'Exit', 'Premium', 'Stock P&L', 'Option P&L', 'Total P&L', 'Exit Reason', 'Days'];
    const rows = processedTrades.map(t => [
      formatDate(t.entry_date),
      formatDate(t.exit_date),
      `$${t.strike_price.toFixed(0)}`,
      t.entry_delta != null ? t.entry_delta.toFixed(2) : '-',
      t.entry_dte,
      `$${t.entry_price.toFixed(2)}`,
      `$${t.exit_price.toFixed(2)}`,
      `$${t.entry_premium.toFixed(2)}`,
      formatCurrency(t.pnl_stock),
      formatCurrency(t.pnl_option),
      formatCurrency(t.pnl_total),
      formatExitReason(t.exit_reason),
      t.days_held
    ].join('\t'));

    const tsv = [headers.join('\t'), ...rows].join('\n');

    try {
      await navigator.clipboard.writeText(tsv);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const SortIndicator = ({ field }: { field: keyof TradeResult }) => {
    if (sortField !== field) return null;
    return (
      <span style={{ marginLeft: '4px' }}>
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const sortTh = (field: keyof TradeResult, label: string, align: '' | 'l' | 'c' = '') => (
    <th
      className={`is-sort${align ? ` ${align}` : ''}${sortField === field ? ' is-sorted' : ''}`}
      onClick={() => handleSort(field)}
      aria-sort={sortField === field ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      {label} <SortIndicator field={field} />
    </th>
  );

  const signTone = (value: number): string => (value >= 0 ? 'is-gain' : 'is-loss');

  // Summary stats
  const wins = trades.filter(t => t.pnl_total > 0);
  const losses = trades.filter(t => t.pnl_total <= 0);
  const winCount = wins.length;
  const lossCount = losses.length;
  const totalStockPnL = trades.reduce((sum, t) => sum + t.pnl_stock, 0);
  const totalOptionPnL = trades.reduce((sum, t) => sum + t.pnl_option, 0);
  const totalPnL = trades.reduce((sum, t) => sum + t.pnl_total, 0);
  const avgPnL = trades.length > 0 ? totalPnL / trades.length : 0;
  const avgWin = winCount > 0 ? wins.reduce((sum, t) => sum + t.pnl_total, 0) / winCount : 0;
  const avgLoss = lossCount > 0 ? losses.reduce((sum, t) => sum + t.pnl_total, 0) / lossCount : 0;
  const avgDays = trades.length > 0 ? trades.reduce((sum, t) => sum + t.days_held, 0) / trades.length : 0;

  if (trades.length === 0) {
    return <p className="bt-empty">No trades found for {strategyName}</p>;
  }

  return (
    <div>
      {/* Summary Row */}
      <div className="bt-tstats">
        <div>
          <span className="bt-tstat-l">Total Trades</span>
          <div className="bt-tstat-v n">{trades.length}</div>
        </div>
        <div>
          <span className="bt-tstat-l">Wins / Losses</span>
          <div className="bt-tstat-v n">
            <span className="is-gain">{winCount}</span>
            <span className="is-t3"> / </span>
            <span className="is-loss">{lossCount}</span>
          </div>
        </div>
        <div title="Cumulative stock gains (capped at strike) across all trades. Higher than Buy & Hold due to reinvesting profits into larger positions.">
          <span className="bt-tstat-l">Stock P&L <i>(?)</i></span>
          <div className={`bt-tstat-v n ${signTone(totalStockPnL)}`}>
            {formatCurrency(totalStockPnL)}
          </div>
        </div>
        <div title="Cumulative option income (extrinsic/time-decay value) across all trades.">
          <span className="bt-tstat-l">Option P&L <i>(?)</i></span>
          <div className={`bt-tstat-v n ${signTone(totalOptionPnL)}`}>
            {formatCurrency(totalOptionPnL)}
          </div>
        </div>
        <div>
          <span className="bt-tstat-l">Total P&L</span>
          <div className={`bt-tstat-v n ${signTone(totalPnL)}`}>
            {formatCurrency(totalPnL)}
          </div>
        </div>
        <div>
          <span className="bt-tstat-l">Avg P&L / Trade</span>
          <div className={`bt-tstat-v n ${signTone(avgPnL)}`}>
            {formatCurrency(avgPnL)}
          </div>
        </div>
        <div>
          <span className="bt-tstat-l">Avg Win</span>
          <div className="bt-tstat-v n is-gain">
            {formatCurrency(avgWin)}
          </div>
        </div>
        <div>
          <span className="bt-tstat-l">Avg Loss</span>
          <div className="bt-tstat-v n is-loss">
            {formatCurrency(avgLoss)}
          </div>
        </div>
        <div>
          <span className="bt-tstat-l">Avg Days</span>
          <div className="bt-tstat-v n">
            {avgDays.toFixed(1)}
          </div>
        </div>
        <button
          type="button"
          className={`bt-btn${copied ? ' is-done' : ''}`}
          onClick={handleCopyTable}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            {copied ? (
              <polyline points="20 6 9 17 4 12" />
            ) : (
              <>
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </>
            )}
          </svg>
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>

      {/* Filter */}
      <div className="bt-filters">
        <div className="seg is-wrap" role="group" aria-label="Filter by exit reason">
          <button
            type="button"
            aria-pressed={filterReason === 'all'}
            onClick={() => setFilterReason('all')}
          >
            All<span className="n">{trades.length}</span>
          </button>
          {exitReasons.map(reason => {
            const count = trades.filter(t => t.exit_reason === reason).length;
            return (
              <button
                key={reason}
                type="button"
                aria-pressed={filterReason === reason}
                onClick={() => setFilterReason(reason)}
              >
                {formatExitReason(reason)}<span className="n">{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Table */}
      <div className="bt-tblw">
        <table className="bt-tbl">
          <thead>
            <tr>
              {sortTh('entry_date', 'Entry Date', 'l')}
              {sortTh('exit_date', 'Exit Date', 'l')}
              {sortTh('strike_price', 'Strike')}
              {sortTh('entry_delta', 'Delta')}
              {sortTh('entry_dte', 'DTE')}
              {sortTh('entry_price', 'Entry')}
              {sortTh('exit_price', 'Exit')}
              {sortTh('entry_premium', 'Premium')}
              {sortTh('pnl_stock', 'Stock P&L')}
              {sortTh('pnl_option', 'Option P&L')}
              {sortTh('pnl_total', 'Total P&L')}
              {sortTh('exit_reason', 'Exit Reason', 'c')}
              {sortTh('days_held', 'Days')}
            </tr>
          </thead>
          <tbody>
            {displayedTrades.map((trade, index) => (
              <tr key={`${trade.entry_date}-${trade.strike_price}-${index}`}>
                <td className="l n is-t2">
                  {formatDate(trade.entry_date)}
                </td>
                <td className="l n is-t2">
                  {formatDate(trade.exit_date)}
                </td>
                <td className="n">
                  ${trade.strike_price.toFixed(0)}
                </td>
                <td className="n is-t2">
                  {trade.entry_delta != null ? trade.entry_delta.toFixed(2) : '-'}
                </td>
                <td className="n is-t2">
                  {trade.entry_dte}d
                </td>
                <td className="n">
                  ${trade.entry_price.toFixed(2)}
                </td>
                <td className="n">
                  ${trade.exit_price.toFixed(2)}
                </td>
                <td className="n">
                  ${trade.entry_premium.toFixed(2)}
                </td>
                <td className={`n ${signTone(trade.pnl_stock)}`}>
                  {formatCurrency(trade.pnl_stock)}
                </td>
                <td className={`n ${signTone(trade.pnl_option)}`}>
                  {formatCurrency(trade.pnl_option)}
                </td>
                <td className={`n is-strong ${signTone(trade.pnl_total)}`}>
                  {formatCurrency(trade.pnl_total)}
                </td>
                <td className="c">
                  <span className="bt-tag">{formatExitReason(trade.exit_reason)}</span>
                </td>
                <td className="n is-t3">
                  {trade.days_held}d
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Show More / Less */}
      {processedTrades.length > 10 && (
        <div className="bt-more">
          <button type="button" className="bt-btn" onClick={() => setShowAll(!showAll)}>
            {showAll ? 'Show Less' : `Show All ${processedTrades.length} Trades`}
          </button>
        </div>
      )}
    </div>
  );
};

export default TradeList;
